import type { 
  GameState, 
  GameConfig, 
  GameCallbacks
} from '../types/game';
import { 
  createInitialGameState, 
  createDefaultConfig, 
  updateBallPhysics, 
  updatePlayerPaddle, 
  updateAIPaddle, 
  updatePlayerPaddleAsAI,
  createServeBall
} from '../utils/gameLogic.js';
import { KeyboardManager } from './useKeyboard.js';
import { SimpleGameLoop } from './useGameLoop.js';

/**
 * Game state manager class
 */
export class GameStateManager {
  private state: GameState;
  private config: GameConfig;
  private callbacks: GameCallbacks;
  private keyboardManager: KeyboardManager;
  private gameLoop: SimpleGameLoop;
  private listeners: Array<(state: GameState) => void> = [];

  constructor(config?: Partial<GameConfig>, callbacks?: GameCallbacks) {
    this.config = { ...createDefaultConfig(), ...config };
    this.callbacks = callbacks || {};
    this.state = createInitialGameState(this.config);
    
    this.keyboardManager = new KeyboardManager();
    this.gameLoop = new SimpleGameLoop(this.update.bind(this), 60);
    
    this.keyboardManager.startListening();
  }

  /**
   * Update game state (called by game loop)
   */
  private update(): void {
    if (!this.state.isPlaying || this.state.isPaused) return;

    // Update player paddle based on game mode
    if (this.state.gameMode === 'ai-vs-ai') {
      // In AI vs AI mode, use AI for player paddle
      this.state.playerPaddle = updatePlayerPaddleAsAI(
        this.state.playerPaddle,
        this.state.ball,
        this.config,
        this.config.canvas.height
      );
    } else {
      // In player vs AI mode, use keyboard input
      const upPressed = this.keyboardManager.isKeyPressed(this.config.controls.upKey);
      const downPressed = this.keyboardManager.isKeyPressed(this.config.controls.downKey);
      
      this.state.playerPaddle = updatePlayerPaddle(
        this.state.playerPaddle,
        upPressed,
        downPressed,
        this.config.canvas.height
      );
    }

    // Update AI paddle
    this.state.aiPaddle = updateAIPaddle(
      this.state.aiPaddle,
      this.state.ball,
      this.config,
      this.config.canvas.height
    );

    // Update ball physics
    const ballUpdate = updateBallPhysics(
      this.state.ball,
      this.state.playerPaddle,
      this.state.aiPaddle,
      this.config.canvas
    );

    this.state.ball = ballUpdate.ball;

    // Handle scoring
    if (ballUpdate.scored) {
      this.handleScore(ballUpdate.scored);
    }

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Handle scoring
   */
  private handleScore(scorer: 'player' | 'ai'): void {
    // Update score
    this.state.score[scorer]++;
    
    // Check for game over
    const winningScore = 11; // Standard pong winning score
    if (this.state.score[scorer] >= winningScore) {
      this.state.gamePhase = 'gameOver';
      this.state.winner = scorer;
      this.state.isPlaying = false;
      
      if (this.callbacks.onGameOver) {
        this.callbacks.onGameOver(scorer, this.state.score);
      }
    } else {
      // Prepare for next serve
      this.state.gamePhase = 'scored';
      this.state.ball = createServeBall(
        this.config, 
        scorer === 'player' ? 'ai' : 'player', // Loser serves
        this.state.playerPaddle,
        this.state.aiPaddle
      );
      
      // Auto-serve after a short delay
      setTimeout(() => {
        this.state.gamePhase = 'playing';
        this.notifyListeners();
      }, 1000);
    }

    if (this.callbacks.onScore) {
      this.callbacks.onScore(scorer, this.state.score);
    }
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.state));
    
    if (this.callbacks.onStateChange) {
      this.callbacks.onStateChange(this.state);
    }
  }

  /**
   * Start the game
   */
  public startGame(): void {
    this.state.isPlaying = true;
    this.state.gamePhase = 'playing';
    this.state.isPaused = false;
    this.gameLoop.start();
    this.notifyListeners();
  }

  /**
   * Pause the game
   */
  public pauseGame(): void {
    this.state.isPaused = true;
    this.gameLoop.stop();
    this.notifyListeners();
  }

  /**
   * Resume the game
   */
  public resumeGame(): void {
    if (!this.state.isPlaying) return;
    
    this.state.isPaused = false;
    this.gameLoop.start();
    this.notifyListeners();
  }

  /**
   * Stop the game
   */
  public stopGame(): void {
    this.state.isPlaying = false;
    this.state.isPaused = false;
    this.gameLoop.stop();
    this.notifyListeners();
  }

  /**
   * Reset the game
   */
  public resetGame(): void {
    this.state = createInitialGameState(this.config);
    this.gameLoop.stop();
    this.notifyListeners();
  }

  /**
   * Get current game state
   */
  public getState(): GameState {
    return { ...this.state };
  }

  /**
   * Get game configuration
   */
  public getConfig(): GameConfig {
    return { ...this.config };
  }

  /**
   * Update game configuration
   */
  public updateConfig(newConfig: Partial<GameConfig>): void {
    this.config = { ...this.config, ...newConfig };
    // Update state gameMode if it changed
    if (newConfig.gameMode && this.state.gameMode !== newConfig.gameMode) {
      this.state.gameMode = newConfig.gameMode;
    }
    this.notifyListeners();
  }

  /**
   * Toggle game mode between player vs AI and AI vs AI
   */
  public toggleGameMode(): void {
    const newMode = this.state.gameMode === 'player-vs-ai' ? 'ai-vs-ai' : 'player-vs-ai';
    this.updateConfig({ gameMode: newMode });
  }

  /**
   * Add a state change listener
   */
  public addListener(listener: (state: GameState) => void): void {
    this.listeners.push(listener);
  }

  /**
   * Remove a state change listener
   */
  public removeListener(listener: (state: GameState) => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    this.gameLoop.stop();
    this.keyboardManager.stopListening();
    this.listeners = [];
  }
}

/**
 * Hook-like interface for game state (to be used with React hooks when available)
 */
export function createGameStateHook() {
  return {
    // This will be implemented when React hooks are available
    useGameState: (_config?: Partial<GameConfig>, _callbacks?: GameCallbacks) => ({
      state: createInitialGameState(createDefaultConfig()),
      startGame: () => {},
      pauseGame: () => {},
      resumeGame: () => {},
      stopGame: () => {},
      resetGame: () => {},
      updateConfig: (_config: Partial<GameConfig>) => {}
    })
  };
} 